import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import {
  ArrowRight, ShieldCheck, Hammer, HeartHandshake, Sprout,
  CheckCircle2, MapPin, Star,
} from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import BeforeAfter from "../components/BeforeAfter";
import { SERVICE_ICONS, IMAGES } from "../data/content";
import { useContent } from "../context/ContentContext";
import { api, API } from "../lib/api";

const galleryImg = (path) => `${API}/gallery/image/${path}`;

const values = [
  { icon: ShieldCheck, title: "Veteran-Owned", text: "Discipline, integrity, and a mission-first mindset on every property we touch." },
  { icon: Hammer, title: "Real Craftsmanship", text: "We sweat the details — clean lines, healthy turf, and work that holds up." },
  { icon: HeartHandshake, title: "Built on Trust", text: "A father-and-son team that treats your yard like our own family's." },
  { icon: Sprout, title: "Growing With You", text: "A local company invested in this community for the long haul." },
];

export default function Home() {
  const { content } = useContent();
  const { hero, contact, services } = content;
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/gallery")
      .then(({ data }) => setGallery((data || []).slice(0, 2)))
      .catch(() => setGallery([]));
    api.get("/testimonials")
      .then(({ data }) => setTestimonials(data || []))
      .catch(() => setTestimonials([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <img src={IMAGES.hero} alt="Perfectly manicured green lawn" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 text-gold text-sm tracking-[0.2em] uppercase font-600 mb-6">
              <span className="gold-divider" /> {hero.eyebrow}
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-700 text-white leading-[1.05] tracking-tight text-balance">
              {hero.titleLine1}
              <span className="block text-gold">{hero.titleHighlight}</span>
            </h1>
            <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-xl">
              {hero.subtext}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/quote"
                data-testid="hero-quote-btn"
                className="group inline-flex items-center justify-center gap-2 bg-emerald text-white px-8 py-4 rounded-sm font-600 tracking-wide hover:bg-emerald-dark transition-colors"
              >
                Request a Free Quote
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/40 text-white px-8 py-4 rounded-sm font-600 tracking-wide hover:bg-white hover:text-charcoal transition-colors"
              >
                Explore Our Services
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-white/75 text-sm">
              {contact.serviceAreas.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-gold" /> {a}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <Reveal><Eyebrow>Why Legacy</Eyebrow></Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-600 text-charcoal mt-4 tracking-tight">
                Not your average lawn service.
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="text-lg text-olive mt-5 leading-relaxed">
                We're a growing local company built on hard work, craftsmanship, and responsibility —
                the same values we'd hand down to the next generation.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i}>
                <div className="h-full bg-white p-8 border border-border rounded-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-testid={`value-card-${i}`}>
                  <span className="grid place-items-center w-12 h-12 rounded-sm bg-emerald/10 text-emerald mb-5">
                    <v.icon className="w-6 h-6" strokeWidth={1.75} />
                  </span>
                  <h3 className="font-display text-xl font-600 text-charcoal mb-2">{v.title}</h3>
                  <p className="text-olive leading-relaxed text-[15px]">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STORY STRIP */}
      <section className="bg-charcoal text-white">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[420px]">
            <img src={IMAGES.founders} alt="Father and son working outdoors" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "50% 22%" }} />
          </div>
          <div className="px-6 md:px-12 lg:px-20 py-20 flex flex-col justify-center">
            <Reveal><Eyebrow>Our Story</Eyebrow></Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-3xl sm:text-4xl font-600 mt-4 mb-6 leading-tight">
                Founded by a father and son who believe in doing it right.
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="text-white/75 leading-relaxed text-lg mb-4">
                Adrian and David started Legacy Lawn &amp; Landscape with a simple idea: treat every
                property with pride, show up when we say we will, and build something that lasts —
                for our customers and for our family.
              </p>
            </Reveal>
            <Reveal delay={3}>
              <Link to="/founders" className="inline-flex items-center gap-2 text-gold font-600 mt-4 hover:gap-3 transition-all" data-testid="story-founders-link">
                Meet Adrian &amp; David <ArrowRight className="w-5 h-5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="max-w-xl">
              <Reveal><Eyebrow>What We Do</Eyebrow></Reveal>
              <Reveal delay={1}>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-600 text-charcoal mt-4 tracking-tight">
                  From weekly mowing to full drainage solutions.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={2}>
              <Link to="/services" className="inline-flex items-center gap-2 text-emerald font-600 hover:gap-3 transition-all" data-testid="services-all-link">
                View all services <ArrowRight className="w-5 h-5" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[i] || SERVICE_ICONS[0];
              return (
              <Reveal key={s.group} delay={i}>
                <div className="h-full bg-white p-8 border border-border rounded-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-testid={`service-group-${i}`}>
                  <span className="grid place-items-center w-12 h-12 rounded-sm bg-gold/15 text-gold-dark mb-5">
                    <Icon className="w-6 h-6" strokeWidth={1.75} />
                  </span>
                  <h3 className="font-display text-xl font-600 text-charcoal mb-3">{s.group}</h3>
                  <p className="text-olive text-[15px] leading-relaxed mb-4">{s.blurb}</p>
                  <ul className="space-y-1.5">
                    {s.items.slice(0, 3).map((it) => (
                      <li key={it.name} className="flex items-start gap-2 text-sm text-charcoal/80">
                        <CheckCircle2 className="w-4 h-4 text-emerald mt-0.5 shrink-0" /> {it.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR WORK TEASER */}
      {gallery.length > 0 && (
        <section className="py-24 md:py-32 bg-white" data-testid="home-gallery-teaser">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div className="max-w-xl">
                <Reveal><Eyebrow>Our Work</Eyebrow></Reveal>
                <Reveal delay={1}>
                  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-600 text-charcoal mt-4 tracking-tight">
                    The proof is in the transformation.
                  </h2>
                </Reveal>
                <Reveal delay={2}>
                  <p className="text-lg text-olive mt-5 leading-relaxed">
                    Drag the slider to see what a little Legacy care can do.
                  </p>
                </Reveal>
              </div>
              <Reveal delay={2}>
                <Link to="/gallery" className="inline-flex items-center gap-2 text-emerald font-600 hover:gap-3 transition-all" data-testid="home-gallery-link">
                  View all our work <ArrowRight className="w-5 h-5" />
                </Link>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              {gallery.map((it, i) => (
                <Reveal key={it.id} delay={i}>
                  <div data-testid={`home-gallery-item-${it.id}`}>
                    <BeforeAfter
                      before={galleryImg(it.before_path)}
                      after={galleryImg(it.after_path)}
                      alt={it.title}
                      testid={`home-gallery-slider-${it.id}`}
                    />
                    <h3 className="font-display text-xl font-600 text-charcoal mt-4">{it.title}</h3>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="py-24 md:py-32 bg-[#F8F9FA]" data-testid="home-testimonials">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-xl mb-16">
              <Reveal><Eyebrow>Kind Words</Eyebrow></Reveal>
              <Reveal delay={1}>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-600 text-charcoal mt-4 tracking-tight">
                  Trusted by neighbors across the Lowcountry.
                </h2>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {testimonials.slice(0, 6).map((t, i) => (
                <Reveal key={t.id} delay={i % 3}>
                  <figure
                    data-testid={`home-testimonial-${t.id}`}
                    className="h-full flex flex-col bg-white border border-border rounded-sm p-7 hover:shadow-md hover:-translate-y-0.5 transition-all"
                  >
                    <div className="flex gap-1 mb-4">
                      {[...Array(t.rating || 5)].map((_, s) => (
                        <Star key={s} className="w-4 h-4 fill-gold text-gold" />
                      ))}
                    </div>
                    <blockquote className="text-charcoal leading-relaxed flex-1">"{t.text}"</blockquote>
                    <figcaption className="mt-5 pt-5 border-t border-border">
                      <span className="font-display font-600 text-charcoal">{t.name}</span>
                      {t.location && <span className="text-olive text-sm"> · {t.location}</span>}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-emerald text-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <div className="flex justify-center gap-1 mb-6">
              {["star-1", "star-2", "star-3", "star-4", "star-5"].map((k) => <Star key={k} className="w-5 h-5 fill-gold text-gold" />)}
            </div>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-600 leading-tight text-balance">
              Ready for a yard you're proud of?
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="text-white/80 text-lg mt-5 max-w-2xl mx-auto">
              Tell us about your property and we'll put together a fair, no-pressure quote.
              {" "}Call us at <span className="text-gold font-600">{contact.phone}</span> or request online.
            </p>
          </Reveal>
          <Reveal delay={3}>
            <Link
              to="/quote"
              data-testid="cta-quote-btn"
              className="inline-flex items-center gap-2 bg-gold text-charcoal px-9 py-4 rounded-sm font-700 tracking-wide mt-9 hover:bg-gold-dark transition-colors"
            >
              Request a Free Quote <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
